import {
  Divider,
  Grid,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { CancelButtonStyled } from '@v2/components/campaign/CampaignAdGroupSection/styles';
import { Button } from '@v2/components/ui/Button';
import { memo } from 'react';
import { ModalSheetDialog as Dialog } from '../styledComponents';
import {
  useForm,
  FormProvider,
  type FieldValues,
  type Resolver,
} from 'react-hook-form';
import { SelectCreativesHeader } from './SelectCreativesHeader';
import { LibraryCreativeTable } from '@v2/components/campaign/CampaignAdGroupSection/AdvancedSubflows/AdvancedCreative/Library/LibraryCreativeTable';
import { CreativeContext } from '@v2/components/campaign/CampaignAdGroupSection/AdvancedSubflows/AdvancedCreative/constants';
import { Creative } from '@local-types';
import { xorBy } from 'lodash';
import { CommonDialogProps } from '@components/modals/BulkEditModal/constants/types';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

export interface SelectCreativesFormValues {
  files: File[];
  creatives: Creative[];
  search: string;
}

export interface SelectCreativesProps extends CommonDialogProps {
  onSubmit: (formData: SelectCreativesFormValues) => void;
  isDisplay: boolean;
  library: Creative[];
  initialCreatives?: Creative[];
  isLoading?: boolean;
}

export const schema = yup.object().shape({
  creatives: yup
    .array()
    .of(yup.object())
    .min(1, 'At least one creative must be selected'),
});

function createValidationResolver<T extends FieldValues>(
  schema: yup.AnyObjectSchema,
): Resolver<T> {
  return yupResolver(schema) as Resolver<T>;
}

export const SelectCreatives = memo(function EditCreatives({
  onSubmit,
  onCancel,
  onBack,
  initialCreatives = [],
  isValidating = false,
  open,
  isDisplay,
  library,
  isLoading,
}: SelectCreativesProps) {
  const methods = useForm<SelectCreativesFormValues>({
    defaultValues: {
      files: [],
      creatives: initialCreatives,
      search: '',
    },
    resolver: createValidationResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  return (
    <CreativeContext.Provider
      value={{
        library: library as never[],
        toggleAssignCreative: (creative: Creative) => {
          methods.setValue(
            'creatives',
            xorBy(methods.getValues('creatives'), [creative], 'id'),
            { shouldValidate: true },
          );
        },
        setTab: () => {},
        tab: {
          key: 'creativeLibrary',
        },
        drop: () => {},
      }}
    >
      <FormProvider {...methods}>
        <Dialog
          open={open}
          onClose={onCancel}
          fullWidth
          maxWidth="lg"
          sx={{ pt: 2, pb: 2 }}
          data-testid="edit-dates-times-dialog"
        >
          <form id="bulk-edit-form" onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              <SelectCreativesHeader
                onBack={onBack}
                onCancel={onCancel}
                isDisplay={isDisplay}
              />
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ maxHeight: '70vh' }}>
                {isLoading ? (
                  <Stack
                    width="100%"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircularProgress />
                  </Stack>
                ) : (
                  <LibraryCreativeTable
                    isDisplay={isDisplay}
                    onClose={() => {
                      onCancel?.();
                    }}
                  />
                )}
              </Box>
            </DialogContent>

            <DialogActions disableSpacing sx={{ px: 4, py: 3 }}>
              <Grid container direction="column" sx={{ width: '100%' }}>
                <Divider sx={{ width: '100%', mb: 3 }} />
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ minHeight: 'auto' }}
                >
                  <Grid>
                    <CancelButtonStyled variant="text" onClick={onCancel}>
                      Cancel
                    </CancelButtonStyled>
                  </Grid>
                  <Grid>
                    <Button
                      variant="contained"
                      type="submit"
                      label="Next: Manage Creative"
                      disabled={!isValid || isValidating}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        </Dialog>
      </FormProvider>
    </CreativeContext.Provider>
  );
});
